import { FC } from 'react'
import styles from './sparkline.module.scss'

interface SparklineProps {
  // Oldest first. Fewer than two points cannot describe a line.
  points: number[]
  // Colours the line: a falling trend is drawn red, a rising one green.
  rising: boolean
}

const WIDTH = 72
const HEIGHT = 28
// Keeps the stroke's round cap inside the viewBox at the extremes.
const PAD = 2

export const Sparkline: FC<SparklineProps> = ({ points, rising }) => {
  if (!points || points.length < 2) return null

  const min = Math.min(...points)
  const max = Math.max(...points)
  const span = max - min

  const x = (index: number) =>
    PAD + (index / (points.length - 1)) * (WIDTH - PAD * 2)
  // A flat series has no span to normalise against. Dividing by it would give
  // NaN and drop the path entirely, so a flat trend is drawn down the middle —
  // which is what "no change" should look like.
  const y = (value: number) =>
    span === 0
      ? HEIGHT / 2
      : HEIGHT - PAD - ((value - min) / span) * (HEIGHT - PAD * 2)

  const path = points
    .map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index)} ${y(value)}`)
    .join(' ')

  return (
    <svg
      aria-hidden="true"
      className={styles.sparkline}
      fill="none"
      preserveAspectRatio="none"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
    >
      <path
        d={path}
        stroke={rising ? '#16a34a' : '#ef4444'}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}
